import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CashflowProjectionSection = ({ formData, handleInputChange, errors }) => {
  const growthScenarioOptions = [
    { value: 'conservative', label: 'Konservatif (5-10% pertumbuhan)' },
    { value: 'moderate', label: 'Moderat (10-20% pertumbuhan)' },
    { value: 'aggressive', label: 'Agresif (20-30% pertumbuhan)' },
    { value: 'custom', label: 'Kustom' }
  ];

  const calculateProjectedCashflow = () => {
    const currentCashflow = parseFloat(formData?.currentMonthlyCashflow) || 0;
    const growthRate = parseFloat(formData?.projectedGrowthRate) || 0;
    return (currentCashflow * (1 + growthRate / 100))?.toFixed(0);
  };

  const calculateBreakEvenPoint = () => {
    const fixedCosts = parseFloat(formData?.monthlyFixedCosts) || 0;
    const variableCostPerUnit = parseFloat(formData?.variableCostPerUnit) || 0;
    const pricePerUnit = parseFloat(formData?.pricePerUnit) || 0;
    
    if (pricePerUnit - variableCostPerUnit === 0) return 0;
    return (fixedCosts / (pricePerUnit - variableCostPerUnit))?.toFixed(0);
  };

  const calculateCashflowGap = () => {
    const inflow = parseFloat(formData?.monthlyInflow) || 0;
    const outflow = parseFloat(formData?.monthlyOutflow) || 0;
    return (inflow - outflow)?.toFixed(0);
  };

  const cashflowGap = parseFloat(calculateCashflowGap());
  const gapStatus = cashflowGap >= 0 ? 
    { color: 'success', label: 'Surplus', icon: 'TrendingUp' } : 
    { color: 'error', label: 'Defisit', icon: 'TrendingDown' };

  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="LineChart" size={24} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Proyeksi Arus Kas</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Simulasi arus kas untuk perencanaan scale-up dan identifikasi risiko likuiditas
          </p>
        </div>
      </div>
      <div className="space-y-6 md:space-y-8">
        <div className="p-4 md:p-6 bg-muted rounded-lg">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Data Arus Kas Saat Ini</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Arus Kas Masuk Bulanan"
              type="number"
              placeholder="0"
              description="Total penerimaan kas"
              value={formData?.monthlyInflow || ''}
              onChange={(e) => handleInputChange('monthlyInflow', null, e?.target?.value)}
              error={errors?.monthlyInflow}
              required
            />

            <Input
              label="Arus Kas Keluar Bulanan"
              type="number"
              placeholder="0"
              description="Total pengeluaran kas"
              value={formData?.monthlyOutflow || ''}
              onChange={(e) => handleInputChange('monthlyOutflow', null, e?.target?.value)}
              error={errors?.monthlyOutflow}
              required
            />

            <Input
              label="Arus Kas Bersih Bulanan"
              type="number"
              placeholder="0"
              description="Kas masuk - kas keluar"
              value={formData?.currentMonthlyCashflow || ''}
              onChange={(e) => handleInputChange('currentMonthlyCashflow', null, e?.target?.value)}
              error={errors?.currentMonthlyCashflow}
              required
            />

            <div className={`p-4 bg-${gapStatus?.color}/10 rounded-lg border-2 border-${gapStatus?.color}/20`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name={gapStatus?.icon} size={20} color={`var(--color-${gapStatus?.color})`} />
                <p className="text-sm text-muted-foreground">Status Arus Kas</p>
              </div>
              <p className="text-xl md:text-2xl font-bold data-text">
                Rp {Math.abs(cashflowGap)?.toLocaleString('id-ID')}
              </p>
              <span className={`text-sm font-medium text-${gapStatus?.color}`}>{gapStatus?.label}</span>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-muted rounded-lg">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Proyeksi Pertumbuhan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Select
              label="Skenario Pertumbuhan"
              description="Pilih target pertumbuhan bisnis"
              options={growthScenarioOptions}
              value={formData?.growthScenario || ''}
              onChange={(value) => handleInputChange('growthScenario', null, value)}
              error={errors?.growthScenario}
              required
            />

            <Input
              label="Tingkat Pertumbuhan (%)"
              type="number"
              placeholder="0"
              description="Proyeksi pertumbuhan tahunan"
              value={formData?.projectedGrowthRate || ''}
              onChange={(e) => handleInputChange('projectedGrowthRate', null, e?.target?.value)}
              error={errors?.projectedGrowthRate}
              required
            />

            <Input
              label="Investasi Scale-Up Diperlukan"
              type="number"
              placeholder="0"
              description="Modal untuk ekspansi"
              value={formData?.scaleUpInvestment || ''}
              onChange={(e) => handleInputChange('scaleUpInvestment', null, e?.target?.value)}
              error={errors?.scaleUpInvestment}
            />

            <div className="p-4 bg-primary/10 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Proyeksi Arus Kas</p>
              <p className="text-xl md:text-2xl font-bold data-text">
                Rp {parseFloat(calculateProjectedCashflow())?.toLocaleString('id-ID')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Per bulan setelah scale-up</p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-muted rounded-lg">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Analisis Break-Even</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Biaya Tetap Bulanan"
              type="number"
              placeholder="0"
              description="Gaji, sewa, dll"
              value={formData?.monthlyFixedCosts || ''}
              onChange={(e) => handleInputChange('monthlyFixedCosts', null, e?.target?.value)}
              error={errors?.monthlyFixedCosts}
            />

            <Input
              label="Biaya Variabel per Unit"
              type="number"
              placeholder="0"
              description="Bahan baku per produk"
              value={formData?.variableCostPerUnit || ''}
              onChange={(e) => handleInputChange('variableCostPerUnit', null, e?.target?.value)}
              error={errors?.variableCostPerUnit}
            />

            <Input
              label="Harga Jual per Unit"
              type="number"
              placeholder="0"
              description="Harga ke konsumen"
              value={formData?.pricePerUnit || ''}
              onChange={(e) => handleInputChange('pricePerUnit', null, e?.target?.value)}
              error={errors?.pricePerUnit}
            />

            <div className="p-4 bg-accent/10 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Titik Impas (Break-Even)</p>
              <p className="text-xl md:text-2xl font-bold data-text">
                {parseFloat(calculateBreakEvenPoint())?.toLocaleString('id-ID')} Unit
              </p>
              <p className="text-sm text-muted-foreground mt-1">Per bulan untuk impas</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-warning/10 rounded-lg border-2 border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium mb-1">Peringatan Risiko Arus Kas:</p>
            <p className="text-muted-foreground">
              Pastikan cadangan kas minimal 3 bulan biaya operasional sebelum scale-up. Proyeksi ini akan dianalisis oleh AI untuk identifikasi risiko tersembunyi.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashflowProjectionSection;